import migrationTokens from '@lessonnine/design-tokens.lib/dist/json/migration.json';
import { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import { Property } from 'csstype';
import cloneDeep from 'lodash/cloneDeep';

import { getIsReskinned } from '../configuration';

interface ArbitraryColorTokenDictionary {
  [key: string]: ArbitraryColorTokenDictionary | Property.Color;
}

const isPropertyColor = (
  target: ArbitraryColorTokenDictionary | Property.Color
): target is Property.Color => typeof target === 'string';

const getTokenByKey = (tokens: BabbelTheme['color'], key: string): Property.Color => {
  const token = key
    .split('.')
    .reduce<ArbitraryColorTokenDictionary[keyof ArbitraryColorTokenDictionary]>(
      (acc, keySegment) => {
        if (isPropertyColor(acc)) {
          throw new Error(
            `Attempted to access key ${key} failed at ${keySegment} as the tokens object was exhausted`
          );
        }
        return acc[keySegment];
      },
      tokens
    );
  if (!isPropertyColor(token)) {
    throw new Error(`The key ${key} did not lead to a leaf token`);
  }
  return token;
};

const updateTokenByKey = (tokens: BabbelTheme['color'], key: string, token: Property.Color) => {
  let tokenToUpdate = tokens as ArbitraryColorTokenDictionary;
  const keySegments = key.split('.');
  const lastKeySegment = keySegments.pop() as string;
  for (const keySegment of keySegments) {
    const nextToken = tokenToUpdate[keySegment];
    if (isPropertyColor(nextToken)) {
      throw new Error(
        `Attempted to access key ${key} failed at ${keySegment} as the tokens object was exhausted`
      );
    }
    tokenToUpdate = nextToken;
  }
  tokenToUpdate[lastKeySegment] = token;
};

export const reskinTokens = (tokens: BabbelTheme['color']): BabbelTheme['color'] => {
  if (!getIsReskinned()) {
    return tokens;
  }
  const reskinnedTokens = cloneDeep(tokens);
  for (const [currentKey, reskinnedKey] of Object.entries(migrationTokens)) {
    const targetToken = getTokenByKey(tokens, reskinnedKey);
    updateTokenByKey(reskinnedTokens, currentKey, targetToken);
  }
  return reskinnedTokens;
};
